import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';

import type { DecisionReason } from '../api/types';
import { prettyValue } from './player/streamText';

/// Every check that ran, in the order it ran.
///
/// The one thing the product promises about a delivery is that it says why it is what it is,
/// and it says it in the same words in both places it is read: the panel somebody opens over
/// their own film, and the card an administrator watches the server through. So the list is
/// written once.
///
/// The code is the translation key; the server's English message is the fallback for anything a
/// locale has not caught up with yet.
export default function DecisionReasons({
  reasons,
  detailed = false,
}: {
  reasons: DecisionReason[];
  /// Names the code and the stream under each line. An administrator looking at somebody
  /// else's session needs the handle to search for; somebody watching a film does not.
  detailed?: boolean;
}) {
  const { t } = useTranslation();

  return (
    <>
      {reasons.map((reason, index) => {
        // The values are written the way the stream lines are written: "E-AC-3", not "eac3".
        const sourceValue = reason.sourceValue ? prettyValue(reason.sourceValue) : reason.sourceValue;
        const clientValue = reason.clientValue ? prettyValue(reason.clientValue) : reason.clientValue;

        const detail = [
          detailed ? reason.code : undefined,
          detailed && reason.streamIndex !== null
            ? t('sessions.streamIndex', { index: reason.streamIndex })
            : undefined,
          sourceValue,
          clientValue,
        ].filter(Boolean);

        return (
          <Box key={`${reason.code}-${index}`}>
            <Typography variant="body2">
              {t(reason.code, { sourceValue, clientValue, defaultValue: reason.message })}
            </Typography>

            {detail.length > 0 && (
              <Typography variant="caption" color="text.secondary" sx={{ overflowWrap: 'anywhere' }}>
                {detail.join('  ·  ')}
              </Typography>
            )}
          </Box>
        );
      })}
    </>
  );
}
